/**
 * Device fingerprint components, comparison and identity derivation.
 *
 * WHY THIS EXISTS
 * A hash of device signals is not an identity. Two people holding the same
 * phone model, on the same OS, in the same timezone, with the same language
 * produce byte-identical signals — so hashing them merges two humans into one
 * user_id. Exact-hash comparison also fails in the opposite direction: plugging
 * in an external monitor, zooming the browser or adding a keyboard language
 * rewrites one signal, the hash changes completely, and one returning user is
 * counted as a brand new one.
 *
 * So the signals are kept as a VECTOR instead of being collapsed into a hash:
 *   1. identity is a random per-install salt — unique even on identical devices
 *   2. the component vector is persisted next to it (the "combination key")
 *   3. on every visit the vector is recollected and compared component by
 *      component with a WEIGHTED score, not equality, so drift is tolerated
 *   4. on a match the stored vector is re-baselined to the current values, so
 *      gradual drift can never accumulate into a break
 *
 * The vector's job is only to answer "does this stored identity belong to the
 * device I am running on?". It does not have to be globally unique — the salt
 * carries the uniqueness — which is why the matcher can afford to be generous.
 */

/** Bump when the component set or record shape changes incompatibly. */
export const RECORD_VERSION = 2;

/**
 * Fraction of comparable weight that must match for "same device".
 * Deliberately generous: the record is read from THIS browser's own storage, so
 * the realistic failure mode is drift (false split), not a stranger's device
 * (false merge). Hard rejection is handled by the anchor rules below instead.
 */
export const MATCH_THRESHOLD = 0.6;

/**
 * Component specification.
 *
 * weight — how much this signal contributes to the match score.
 * anchor — hardware-bound signals that do not change on a given device. Two or
 *          more anchor mismatches is an automatic reject regardless of score;
 *          `platform` alone is enough (an OS class never changes under a
 *          browser profile).
 *
 * `screen` is deliberately NOT an anchor: a laptop reports the dimensions of
 * whichever display the window currently sits on, so an external monitor would
 * otherwise read as a different device.
 */
export const COMPONENT_SPEC = [
  { key: 'platform', weight: 3, anchor: true },
  { key: 'cores', weight: 2, anchor: true },
  { key: 'touch', weight: 2, anchor: true },
  { key: 'memory', weight: 2, anchor: true },
  { key: 'tz', weight: 2, anchor: false },
  { key: 'lang', weight: 2, anchor: false },
  { key: 'screen', weight: 2, anchor: false },
  { key: 'colorDepth', weight: 1, anchor: false },
  { key: 'mobile', weight: 1, anchor: false },
  // Volatile: taskbar/dock changes, browser zoom and language-list edits move
  // these constantly. Kept for signal, weighted low so they cannot split a user.
  { key: 'avail', weight: 1, anchor: false },
  { key: 'dpr', weight: 1, anchor: false },
  { key: 'langs', weight: 1, anchor: false }
];

/** Read a signal without letting a hardened or exotic browser throw. */
function safe(fn) {
  try {
    const value = fn();
    if (value === undefined || value === null || value === '') return undefined;
    return String(value);
  } catch (e) {
    return undefined;
  }
}

/**
 * Orientation-independent dimension pair: a phone rotated to landscape must not
 * read as a different screen.
 */
function dimensions(a, b) {
  const w = Number(a);
  const h = Number(b);
  if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return undefined;
  return [w, h].sort((x, y) => y - x).join('x');
}

/**
 * Collect the current device's component vector.
 * Synchronous by design — identity resolves before the first event is queued,
 * so nothing ships with a placeholder id.
 * @returns {Object<string,string|undefined>}
 */
export function collectComponents() {
  const intl = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions(); } catch (e) { return {}; }
  })();
  const uaData = (typeof navigator !== 'undefined' && navigator.userAgentData) || null;

  return {
    // Chromium exposes a clean OS name; elsewhere navigator.platform is the
    // closest stable equivalent ("MacIntel", "Win32", "Linux armv8l").
    platform: safe(() => (uaData && uaData.platform) || navigator.platform),
    cores: safe(() => navigator.hardwareConcurrency),
    touch: safe(() => navigator.maxTouchPoints),
    memory: safe(() => navigator.deviceMemory),
    tz: safe(() => intl.timeZone),
    // Primary language only. The full list is a separate, lower-weight signal
    // because installing a keyboard rewrites it.
    lang: safe(() => (navigator.language || '').toLowerCase()),
    screen: safe(() => dimensions(screen.width, screen.height)),
    colorDepth: safe(() => screen.colorDepth),
    mobile: safe(() => (uaData && typeof uaData.mobile === 'boolean' ? String(uaData.mobile) : undefined)),
    avail: safe(() => dimensions(screen.availWidth, screen.availHeight)),
    // Browser zoom changes this, hence the low weight.
    dpr: safe(() => Math.round(window.devicePixelRatio * 100) / 100),
    langs: safe(() => (navigator.languages && navigator.languages.join(',')) || undefined)
  };
}

/**
 * Short, stable, synchronous hash. Used ONLY for per-component equality in the
 * stored record — it is never an identity, so 32 bits of collision resistance
 * is ample and the value keeps raw device data out of storage.
 */
export function shortHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return (hash >>> 0).toString(36);
}

/**
 * Hash a component vector for storage. Undefined components are omitted so the
 * comparator can tell "signal absent" apart from "signal changed".
 */
export function hashComponents(components) {
  const out = {};
  COMPONENT_SPEC.forEach(({ key }) => {
    const value = components[key];
    if (value !== undefined) out[key] = shortHash(key + ':' + value);
  });
  return out;
}

/**
 * Weighted comparison of a stored vector against the current one.
 *
 * A component present on only one side is skipped entirely rather than counted
 * as a mismatch: an API appearing or disappearing (navigator.deviceMemory
 * behind a flag, userAgentData arriving in a browser update) is a browser
 * version event, not a device change, and must never split a user.
 *
 * @returns {{isMatch:boolean, score:number, changed:string[], skipped:string[],
 *            anchorMismatches:number, anchorsCompared:number, comparedWeight:number}}
 */
export function compareComponents(stored, current) {
  const changed = [];
  const skipped = [];
  let matchedWeight = 0;
  let comparedWeight = 0;
  let anchorMismatches = 0;
  let anchorsCompared = 0;
  let platformChanged = false;

  COMPONENT_SPEC.forEach(({ key, weight, anchor }) => {
    const before = stored ? stored[key] : undefined;
    const now = current ? current[key] : undefined;

    if (before === undefined || now === undefined) {
      if (before !== now) skipped.push(key);
      return;
    }

    comparedWeight += weight;
    if (anchor) anchorsCompared++;
    if (before === now) {
      matchedWeight += weight;
      return;
    }

    changed.push(key);
    if (anchor) anchorMismatches++;
    if (key === 'platform') platformChanged = true;
  });

  const score = comparedWeight > 0 ? matchedWeight / comparedWeight : 0;

  // Full-anchor pass: when the hardware anchors are all comparable-and-equal,
  // the machine is the same machine — and the record can only have been
  // written by this same browser profile in the first place. Soft signals may
  // then drift arbitrarily (the "back from a trip" visit reverts timezone,
  // monitor, zoom and keyboard language all at once) without splitting the
  // user. The score threshold only decides when anchor evidence is thin.
  const anchorsAllMatch = anchorsCompared >= 3 && anchorMismatches === 0;

  const isMatch =
    comparedWeight > 0 &&
    !platformChanged &&
    anchorMismatches < 2 &&
    (anchorsAllMatch || score >= MATCH_THRESHOLD);

  return { isMatch, score, changed, skipped, anchorMismatches, anchorsCompared, comparedWeight };
}

/**
 * 128 bits of randomness, hex encoded. THIS is the identity: it is what makes
 * two physically identical devices resolve to different user ids.
 */
export function generateSalt() {
  try {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (e) {
    /* fall through */
  }
  // Fallback for environments without WebCrypto. Weaker, but still far more
  // entropy than a device-signal hash carries.
  let out = '';
  while (out.length < 32) {
    out += Math.random().toString(16).slice(2);
  }
  return out.slice(0, 32);
}

/** Shape guard for a record read back from storage (or a parent-domain cookie). */
export function isValidRecord(record) {
  return !!record &&
    typeof record === 'object' &&
    record.v === RECORD_VERSION &&
    typeof record.i === 'string' && record.i.length > 0 &&
    !!record.c && typeof record.c === 'object';
}
